import React, { useContext } from "react";
// https://github.com/moment/moment/issues/4877
// eslint-disable-next-line
import { useQueryParams, IQueryParamsType } from "../../hooks/useQueryParams";
import { useOrganizationContext } from "../../contexts/OrganizationProvider";
import { OptionType } from "../../types/GeneralTypes";
import gql from "graphql-tag";
import { useLazyQuery } from "@apollo/react-hooks";
import { sortByKey } from "../../helpers/sortByKey";
import { logErrorToLogdna } from "../../helpers/logdna";
import { ApolloError } from "apollo-boost";

interface IContextType {
  selectedEventTypes: Array<string>;
  eventTypeOptions: Array<OptionType>;
  setSelectedEventTypes: (arg0: Array<string>) => void;
  sortBy: string;
  sortByOptions: Array<OptionType>;
  setSortBy: (arg0: string) => void;
  page?: number;
  setPage: (arg0?: number) => void;
  resetAllFilters: () => void;
}

const ERROR_CODES = {
  updatingAlertsFiltersProvider: "UPDATING_ALERTS_FILTERS_PROVIDER"
};

const notInitializedError = () => {
  throw new Error("Alerts Filters context has not yet been initialized.");
};

const INITIAL_CONTEXT: IContextType = {
  selectedEventTypes: [],
  eventTypeOptions: [],
  setSelectedEventTypes: () => {
    notInitializedError();
  },
  sortBy: "TIME_DESC",
  sortByOptions: [],
  setSortBy: () => {
    notInitializedError();
  },
  page: 1,
  setPage: () => {
    notInitializedError();
  },
  resetAllFilters: () => {
    notInitializedError();
  }
};

export const AlertsFiltersContext = React.createContext(INITIAL_CONTEXT);
export const useAlertsFiltersContext = () => useContext(AlertsFiltersContext);

export const eventTypeFilterName = "event-type-filter";

type PropsType = {
  children: React.ReactNode;
};

export const AlertsFiltersProvider = ({ children }: PropsType) => {
  const { setQueryParams, queryParams } = useQueryParams();

  const sortByOptions = [
    { value: "TIME_DESC", label: "Newest" },
    { value: "TIME_ASC", label: "Oldest" }
  ];

  const [eventTypeOptions, setEventTypeOptions] = React.useState<
    Array<OptionType>
  >([]);

  const { activeOrg } = useOrganizationContext();

  const [getEventTypes, eventListResponse] = useLazyQuery(
    gql`
      query eventTypes($filters: EventFiltersInput) {
        events(filters: $filters) {
          eventTypes
        }
      }
    `,
    {
      fetchPolicy: "cache-and-network",
      onError: (err: ApolloError) => {
        logErrorToLogdna(
          ERROR_CODES.updatingAlertsFiltersProvider,
          "Apollo error while adding comment.",
          err
        );
        return err;
      }
    }
  );

  React.useEffect(() => {
    if (activeOrg) {
      getEventTypes({
        variables: {
          filters: {
            pipelines: activeOrg.pipelines.map(p => p.id),
            archived: false
          }
        }
      });
    }
  }, [activeOrg, getEventTypes]);

  React.useEffect(() => {
    if (!eventListResponse.loading && eventListResponse.data) {
      const currentOptions: Array<OptionType> = eventListResponse.data.events.eventTypes
        .map((e: string) => ({
          label: e.charAt(0).toUpperCase() + e.slice(1),
          value: e
        }))
        .sort(sortByKey<OptionType, "label">("label"));

      if (JSON.stringify(currentOptions) !== JSON.stringify(eventTypeOptions)) {
        setEventTypeOptions(currentOptions);
      }
    }
  }, [eventTypeOptions, eventListResponse]);

  const setNewFilter = (filters: IQueryParamsType) => {
    setQueryParams({ ...filters, page: undefined }, "push");
  };

  const resetAllFilters = () => {
    setQueryParams(
      {
        types: undefined,
        sortBy: undefined,
        page: undefined
      },
      "push"
    );
  };

  const setSelectedEventTypes = (newEventTypes: Array<string>) => {
    if (Array.isArray(newEventTypes) && newEventTypes.length === 0) {
      setNewFilter({ types: undefined });
    } else {
      setNewFilter({ types: newEventTypes });
    }
  };

  const setSortBy = (newSortBy: string) => {
    if (!newSortBy) {
      setNewFilter({ sortBy: undefined });
    } else {
      setNewFilter({ sortBy: newSortBy });
    }
  };

  const setPage = (page?: number) => {
    if (page) {
      setQueryParams({ page }, "push");
    } else {
      setQueryParams({ page: undefined }, "push");
    }
  };

  return (
    <AlertsFiltersContext.Provider
      value={{
        selectedEventTypes:
          queryParams.types && queryParams.types.length
            ? queryParams.types.split(",")
            : [],
        eventTypeOptions,
        setSelectedEventTypes,
        sortBy: queryParams.sortBy ? queryParams.sortBy : "TIME_DESC",
        sortByOptions,
        setSortBy,
        page: queryParams.page ? queryParams.page : undefined,
        setPage,
        resetAllFilters
      }}
    >
      {children}
    </AlertsFiltersContext.Provider>
  );
};

export default AlertsFiltersProvider;
